import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import AppRouter from "./routes";
import store from "./store";
import * as serviceWorker from "./serviceWorker";
import * as EVENT from "./utils/eventKeys";
import { storage as LocalStorage } from "services/config/storage";
import { Analytics } from "./services/analytics";
import { v4 as uuidv4 } from "uuid";
import "antd/dist/antd.css";
import "./index.css";
import "./assets/stylesheet/global.scss";
import { globalQueryClient } from "./utils/queryClient";
import { STORAGE_KEYS } from "./utils/storageKeys";
import { generateDeviceId } from "./utils/commonUtils";
import { sessionEndApi } from "./services/home";
import { QueryClientProvider } from "@tanstack/react-query";
import { checkAndStartSession } from "./store/home/actions";

const uuid = uuidv4();
let isPageLoaded = false;


// Store references to event listeners for cleanup
const eventListeners = {
  loadHandler: null,
  beforeUnloadHandler: null,
  unloadHandler: null,
};

function handleTabClose(event) {
  if (!isPageLoaded) return;

  const sessionStart = sessionStorage.getItem(STORAGE_KEYS.SESSION_START);

  if (sessionStart) {
    const sessionObj = {
      started_at: new Date(JSON.parse(sessionStart)),
      ended_at: new Date(),
    };
    sessionEndApi(sessionObj)
    try {
      Analytics.onlogOut({
        start: sessionObj.started_at,
        end: sessionObj.ended_at,
        session_end_type: EVENT.SESSION_END_TYPE_APP_CLOSED
      });
    } catch (error) {
      console.error("Failed to log tab close event:", error);
    }
  }
  event.returnValue = "";
}

const setupSessionListeners = () => {
  const loadHandler = () => {
    isPageLoaded = true;
  };

  const beforeUnloadHandler = handleTabClose;

  const unloadHandler = () => {
    isPageLoaded = false;
  };

  window.addEventListener("load", loadHandler);
  window.addEventListener("beforeunload", beforeUnloadHandler);
  window.addEventListener("unload", unloadHandler);

  // Store references for cleanup
  eventListeners.loadHandler = loadHandler;
  eventListeners.beforeUnloadHandler = beforeUnloadHandler;
  eventListeners.unloadHandler = unloadHandler;

  // Return cleanup function
  return () => {
    if (eventListeners.loadHandler) {
      window.removeEventListener("load", eventListeners.loadHandler);
      eventListeners.loadHandler = null;
    }
    if (eventListeners.beforeUnloadHandler) {
      window.removeEventListener("beforeunload", eventListeners.beforeUnloadHandler);
      eventListeners.beforeUnloadHandler = null;
    }
    if (eventListeners.unloadHandler) {
      window.removeEventListener("unload", eventListeners.unloadHandler);
      eventListeners.unloadHandler = null;
    }
  };
};

/**
 * Initialize analytics and tracking
 */
const initializeAnalytics = () => {
  const childId = LocalStorage.fetch.defaultChild()?.id;

  if (childId) {
    Analytics.onLogin();
  }

  const payload = {
    [EVENT.DEVICE_ID]: localStorage.getItem(STORAGE_KEYS.DEVICE_ID) || generateDeviceId(),
  };

  Analytics.logEvents(EVENT.APP_LAUNCHED, payload);
};

/*
 * Initialize device ID (once per device)
 */
const initializeDeviceId = () => {
  if (!localStorage.getItem(STORAGE_KEYS.DEVICE_ID)) {
    const deviceId = generateDeviceId();
    localStorage.setItem(STORAGE_KEYS.DEVICE_ID, deviceId);
  }
};

/**
 * Initialize user session and fetch child data if authenticated
 */
const initializeUserSession = () => {
  const authToken = LocalStorage.fetch.authToken();

  if (!authToken) return;

  // Set session start time
  checkAndStartSession()(store.dispatch);
};


function mainRender() {
  // Initialize all services
  initializeDeviceId();
  initializeAnalytics();
  initializeUserSession();

  const cleanupSessionListeners = setupSessionListeners();

  if (typeof window !== 'undefined') {
    window.__HELLO_CLEANUP__ = () => {
      cleanupSessionListeners();
    };
  }

  const container = document.getElementById("root");
  // const root = createRoot(container);

  ReactDOM.render(
    <Provider store={store}>
      <QueryClientProvider client={globalQueryClient}>
        <AppRouter />
      </QueryClientProvider>
    </Provider>,
    container
  );
}

// Adding the rended to setTimeout with zero millisec to tackle FOUC
if (process.env.NODE_ENV !== "production") {
  LocalStorage.set.sessionData(uuid);
  // Workaround for https://github.com/facebook/create-react-app/issues/6399
  // until it gets fixed upstream
  setTimeout(() => {
    mainRender();
  }, 0);
} else {
  LocalStorage.set.sessionData(uuid);
  mainRender();
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
